import React, { useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { URLS } from '@wix/da-url';
import camelCase from 'lodash/camelCase';
import { PapiCorePackage, PapiSubscription } from '@wix/da-papi-types';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import CoreLabel from '@wix/da-ds/pkg/Indicators/CoreLabel';
import ArrowDown from '@wix/da-ds/pkg/Icons/ArrowDown';
import ArrowUp from '@wix/da-ds/pkg/Icons/ArrowUp';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import BillingCyclePopup from '../../../Popup/BillingCyclePopup';
import BenefitsList from './_partials/BenefitsList';
import CardButton from './_partials/CardButton';
import { CoreTypes } from '../../../types';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';
import { getRecommendedPackageType } from '../../../utils';
import { packagesData } from '../../../constants';

import s from './MobileHeroCard.scss';

export interface Props {
  coreType: CoreTypes;
  packages: PapiCorePackage[];
  pickerMode: PackagePickerStoreSlice['pickerMode'];
  alreadyHasSubscription: boolean;
  currentCoreSubscription?: PapiSubscription;
  sale: PackagePickerStoreSlice['sale'];
  packageConfig?: PackagePickerStoreSlice['packageConfig'];
  packageBenefits?: PackagePickerStoreSlice['packageBenefits'];
  referer: string;
}

const GET_NOTIFIED_URL =
  'https://www.deviantartsupport.com/en/article/changing-your-core-membership-package';

const MobileHeroCard: React.FC<Props> = ({
  coreType,
  packages,
  pickerMode,
  packageConfig,
  packageBenefits,
  alreadyHasSubscription,
  currentCoreSubscription,
  sale,
  referer,
}) => {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<number[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const isOpenByDefault =
    (!alreadyHasSubscription && coreType === CoreTypes.CORE_PRO) ||
    (alreadyHasSubscription &&
      currentCoreSubscription?.corePackage?.coreType === coreType);

  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const openBilling = () => setIsPopupOpen(true);

  const monthlyPackage = packages.filter(
    p => p.coreType === coreType && p.interval === 'month'
  )[0];
  const yearlyPackage = packages.filter(
    p => p.coreType === coreType && p.interval === 'year'
  )[0];

  const isPackageDisabled =
    packageConfig?.[camelCase(coreType)]?.enabled === false;

  // Discount might apply to monthly or yearly packages only, or both.
  const hasDiscount = Boolean(
    (yearlyPackage.discount || monthlyPackage.discount) && sale?.ppShowDiscount
  );
  const isGift = pickerMode === 'gift';

  const showCTA =
    isGift || !currentCoreSubscription || currentCoreSubscription.isUpgradable;

  const handleSelect = selected => {
    if (monthlyPackage.status === 'manage') {
      window.location.assign(URLS.SETTINGS_REBILLING);
      return;
    }
    if (monthlyPackage.status === 'notify') {
      window.location.assign(GET_NOTIFIED_URL);
      return;
    }

    setSelectedPackage(selected);
    openBilling();
  };

  const isCurrent =
    alreadyHasSubscription &&
    currentCoreSubscription?.corePackage?.coreType === coreType;

  const isRecommended = getRecommendedPackageType(packageConfig) === coreType;

  return (
    <div
      className={cn(
        s['root'],
        isRecommended && s['with-frame'],
        hasDiscount && s['with-discount']
      )}
    >
      <div
        className={cn(s['main'], (isCurrent || isRecommended) && s['with-tag'])}
      >
        {hasDiscount && (
          <div
            className={cn(s['sale-badge'], isRecommended && s['highlighted'])}
          >
            {t('packagePicker.coreOffer.saleBadge', {
              discountPercent: yearlyPackage.discount
                ? yearlyPackage.discountPercent
                : monthlyPackage.discountPercent,
            })}
          </div>
        )}
        {isCurrent && (
          <div className={s['tag']}>
            {t('packagePicker.coreOffer.currentPlan')}
          </div>
        )}
        {isRecommended && !isCurrent && (
          <div className={s['tag']}>{t('packagePicker.coreOffer.popular')}</div>
        )}
        <div className={s['core-logo']}>
          <CoreSymbol
            corePackage={monthlyPackage.coreType as any}
            size={IconSize.LARGE}
          />
          <CoreLabel
            corePackage={monthlyPackage.coreType as any}
            size="large"
          />
        </div>
        <div className={s['title']}>
          {t(packagesData[monthlyPackage.coreType].title)}
        </div>
        <div className={s['price-wrapper']}>
          <div className={s['price']}>
            {hasDiscount
              ? formatDollars(
                  yearlyPackage.discount
                    ? yearlyPackage.monthlyPrice
                    : monthlyPackage.price
                )
              : formatDollars(
                  monthlyPackage.priceBeforeDiscount ?? monthlyPackage.price
                )}
          </div>
          <div className={s['period']}>
            {t('packagePicker.coreOffer.perMonth')}
          </div>
        </div>
        {hasDiscount && (
          <div className={s['price-before']}>
            {formatDollars(
              monthlyPackage.priceBeforeDiscount ?? monthlyPackage.price
            )}
          </div>
        )}

        {showCTA && (
          <CardButton
            className={s['button']}
            coreType={coreType}
            pickerMode={pickerMode}
            alreadyHasSubscription={alreadyHasSubscription}
            monthlyPackage={monthlyPackage}
            yearlyPackage={yearlyPackage}
            isRecommended={isRecommended}
            isCurrent={isCurrent}
            onSelect={handleSelect}
            isDisabled={isPackageDisabled}
          />
        )}

        <BenefitsList
          coreType={coreType}
          isOpen={isOpen}
          referer={referer}
          packageBenefits={packageBenefits}
        />

        <TextButton
          size={'small'}
          variant={'main'}
          postfix={isOpen ? ArrowUp : ArrowDown}
          onClick={() => setIsOpen(current => !current)}
        >
          {isOpen ? 'Show Less' : 'Show More'}
        </TextButton>

        <BillingCyclePopup
          selectedPackage={selectedPackage}
          coreType={monthlyPackage.coreType as CoreTypes}
          isOpen={isPopupOpen}
          onRequestClose={() => setIsPopupOpen(false)}
          withCloseButton
        />
      </div>
    </div>
  );
};
MobileHeroCard.displayName = 'MobileHeroCard';

export default MobileHeroCard;
